import { AgentChatAttachment, AgentChatMessage, AgentChatSubmission, AgentChatbotProps } from "../types";

import { useAutoScroll } from "./useAutoScroll";
import { useChatAttachments } from "./useChatAttachments";
import { useChatInput } from "./useChatInput";
import { useChatMessages } from "./useChatMessages";
import { usePanelResize } from "./usePanelResize";

interface AgentChatbotController {
  readonly currentMessages: readonly AgentChatMessage[];
  readonly currentAttachedFiles: readonly AgentChatAttachment[];
  readonly currentInputValue: string;
  readonly hasMessages: boolean;
  readonly canSubmit: boolean;
  readonly loadingNotice: string | undefined;
  readonly scrollRegionRef: ReturnType<typeof useAutoScroll>["scrollRegionRef"];
  readonly isScrolledUp: boolean;
  readonly handleScroll: () => void;
  readonly scrollToLatest: () => void;
  readonly panelRef: ReturnType<typeof usePanelResize>["panelRef"];
  readonly panelHeight: number | null;
  readonly minHeight: number;
  readonly onGrabberPointerDown: ReturnType<typeof usePanelResize>["onGrabberPointerDown"];
  readonly onGrabberKeyDown: ReturnType<typeof usePanelResize>["onGrabberKeyDown"];
  readonly textareaRef: ReturnType<typeof useChatInput>["textareaRef"];
  readonly handleTextareaChange: ReturnType<typeof useChatInput>["handleTextareaChange"];
  readonly handleTextareaKeyDown: ReturnType<typeof useChatInput>["handleTextareaKeyDown"];
  readonly handleSubmit: ReturnType<typeof useChatInput>["handleSubmit"];
  readonly handleFilesSelected: (files: FileList) => void;
  readonly handleFileDrop: (files: FileList) => void;
  readonly handleRemoveFile: (id: string) => void;
  readonly populateInput: (value: string) => void;
}

const toUndefined = <T,>(value: T | null | undefined): T | undefined => (value === null || value === undefined ? undefined : value);

/**
 * Centralizes AgentChatbot state and event wiring so the main component can focus on panel layout
 * and markup. All controlled/uncontrolled normalization is handled here.
 */
export const useAgentChatbotController = ({
  messages,
  isLoading,
  hasError,
  attachedFiles,
  onFilesSelected,
  onFileDrop,
  onRemoveFile,
  inputValue,
  onInputChange,
  onSubmit,
}: Pick<
  AgentChatbotProps,
  "messages" | "isLoading" | "hasError" | "attachedFiles" | "onFilesSelected" | "onFileDrop" | "onRemoveFile" | "inputValue" | "onInputChange" | "onSubmit"
>): AgentChatbotController => {
  const { messages: currentMessages, appendUserMessage } = useChatMessages({ messages: toUndefined(messages) });

  const { scrollRegionRef, isScrolledUp, handleScroll, scrollToLatest } = useAutoScroll({
    messageCount: currentMessages.length,
    isLoading: Boolean(isLoading),
    hasError: Boolean(hasError),
  });

  const { panelRef, panelHeight, minHeight, onGrabberPointerDown, onGrabberKeyDown } = usePanelResize();

  const {
    attachedFiles: currentAttachedFiles,
    handleFilesSelected,
    handleFileDrop,
    handleRemoveFile,
    clearAttachments,
  } = useChatAttachments({
    attachedFiles: toUndefined(attachedFiles),
    onFilesSelected: toUndefined(onFilesSelected),
    onFileDrop: toUndefined(onFileDrop),
    onRemoveFile: toUndefined(onRemoveFile),
  });

  const handleMessageSubmit = (submission: AgentChatSubmission) => {
    appendUserMessage(submission.content, submission.attachments);
    clearAttachments();
    toUndefined(onSubmit)?.(submission);
  };

  const {
    textareaRef,
    currentInputValue,
    canSubmit,
    loadingNotice,
    populateInput,
    handleTextareaChange,
    handleTextareaKeyDown,
    handleSubmit,
  } = useChatInput({
    inputValue: toUndefined(inputValue),
    onInputChange: toUndefined(onInputChange),
    onSubmit: handleMessageSubmit,
    isLoading: Boolean(isLoading),
    attachedFiles: currentAttachedFiles,
  });

  return {
    currentMessages,
    currentAttachedFiles,
    currentInputValue,
    hasMessages: currentMessages.length > 0,
    canSubmit,
    loadingNotice,
    scrollRegionRef,
    isScrolledUp,
    handleScroll,
    scrollToLatest,
    panelRef,
    panelHeight,
    minHeight,
    onGrabberPointerDown,
    onGrabberKeyDown,
    textareaRef,
    handleTextareaChange,
    handleTextareaKeyDown,
    handleSubmit,
    handleFilesSelected,
    handleFileDrop,
    handleRemoveFile,
    populateInput,
  };
};
